import { makeAutoObservable } from 'mobx';
import { fromPromise, IPromiseBasedObservable } from 'mobx-utils';
import QueryResponse, { QueryResponseDto } from './QueryResponse';

export default class Query<
  T,
  TResult = QueryResponse<T>,
  K extends keyof T = keyof T,
> {
  filters: Partial<Record<K, T[K]>> = {};

  pagination = {
    limit: 20,
    offset: 0,
    legacy: true,
  };

  orderBy = '';

  protected queryFunction: QueryFunction<T>;
  protected transformFunction: TransformFunction<T, TResult>;

  promise?: IPromiseBasedObservable<TResult> = undefined;
  response?: QueryResponse<T> = undefined;

  constructor({
    params = {},
    queryFunction,
    transformFunction,
  }: {
    params?: QueryParams;
    queryFunction: QueryFunction<T>;
    transformFunction?: TransformFunction<T, TResult>;
  }) {
    makeAutoObservable(this, {}, { autoBind: true });

    this.queryFunction = queryFunction;
    this.transformFunction =
      transformFunction || (this.defaultTransform as any);

    const {
      autoRun = true,
      legacy = false,
      limit,
      offset,
      ...filters
    } = params;

    this.pagination.limit = Number.isFinite(limit) ? (limit as number) : 20;
    this.pagination.offset = Number.isFinite(offset) ? (offset as number) : 0;
    this.pagination.legacy = legacy === true;

    this.filters = (filters || {}) as any;

    if (autoRun) {
      this.execute();
    }
  }

  get isExecuted() {
    return Boolean(this.promise);
  }

  execute(): IPromiseBasedObservable<TResult> {
    this.promise = fromPromise(
      this.queryFunction(this.toJSON()).then((dto) => this.createResponse(dto)),
    );
    return this.promise;
  }

  setFilter(key: string, value: any) {
    Object.assign(this.filters, { [key]: value });
    this.pagination.offset = 0;
  }

  setFilters(filters: Record<string, any>) {
    if (!filters) {
      return;
    }

    Object.entries(filters).forEach(([key, value]) =>
      this.setFilter(key, value),
    );
  }

  setOrderBy(field: string) {
    this.orderBy = field;
    this.pagination.offset = 0;
  }

  toJSON(): QueryParams {
    const { pagination } = this;
    const limit = pagination.limit + (pagination.legacy ? 1 : 0);

    return {
      ...this.filters,
      ...this.pagination,
      ...(this.orderBy && { orderBy: this.orderBy }),
      limit,
    };
  }

  private defaultTransform(results: QueryResponse<T>): QueryResponse<T> {
    return results;
  }

  private createResponse(dto: QueryResponseDto<T>): TResult {
    this.response = new QueryResponse(this as any, dto);
    return this.transformFunction
      ? this.transformFunction(this.response)
      : (this.response as any);
  }
}

type QueryFunction<T> = (
  params: QueryParams,
) => PromiseLike<QueryResponseDto<T>>;
type TransformFunction<T, TResult> = (response: QueryResponse<T>) => TResult;

export type QueryParams<T = any> = {
  autoRun?: boolean;
  limit?: number;
  offset?: number;
} & Partial<T>;
